const API_BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:8080/daas-ws';
const API_EMAIL_SERVER = process.env.REACT_APP_EMAIL_SERVER_URL || 'http://localhost:8080/daas-ws';

// API utility class for making secure HTTP requests
import { startLoginRedirect } from './authRedirect';

class ApiService {
  private readonly baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  /**
   * Makes an HTTP request with proper error handling
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Accept': 'application/json',
      ...options.headers as Record<string, string>,
    };

    // Only set Content-Type to application/json if not FormData and not already set
    if (!(options.body instanceof FormData) && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    const config: RequestInit = {
      ...options,
      headers,
      credentials: 'include', // Include cookies for authentication
    };

    try {
      const response = await fetch(url, config);
      const contentType = response.headers.get('Content-Type') || '';
      const text = await response.text();

      // If not OK, try to extract a meaningful error message from JSON body
      if (!response.ok) {
        let serverMsg: string | undefined;
        try {
          if (text) {
            const looksLikeJson = contentType.includes('application/json') || /^\s*[\[{]/.test(text);
            if (looksLikeJson) {
              const json = JSON.parse(text);
              serverMsg = json?.message || json?.error || json?.msg;
            }
          }
        } catch (_) {
          // ignore JSON parse errors and fall through to raw text
        }
        // If serverMsg not found and the body is non-empty and not HTML, use raw text
        const isHtml = text && (contentType.includes('text/html') || text.startsWith('<!DOCTYPE'));
        const finalMsg = serverMsg || (!isHtml && text ? text : `HTTP error! status: ${response.status}`);
        const err = new Error(finalMsg);
        // @ts-ignore attach optional extras for callers if needed
        (err as any).status = response.status;
        throw err;
      }

      // Handle empty responses
      if (!text) {
        return {} as T;
      }

      // Check if response is HTML (redirect or error page)
      if ((contentType && contentType.includes('text/html')) || text.startsWith('<!DOCTYPE')) {
        throw new Error('Received HTML response instead of JSON. You may not be authenticated or the endpoint is misconfigured.');
      }

      return JSON.parse(text);
    } catch (error) {
      // Intercept 401 to redirect to login and preserve return path
      const status = (error as any)?.status;
      if (status === 401 && typeof window !== 'undefined') {
        try { localStorage.setItem('ud_isAuthenticated', 'false'); } catch (_) { /* ignore */ }
        startLoginRedirect();
        throw new Error('Please log in to continue');
      }

      // Normalize network/connection failures to a user-friendly message
      // Common fetch network errors across browsers: TypeError: Failed to fetch, NetworkError when attempting to fetch resource,
      // net::ERR_* (surface as TypeError), and messages like "Failed to load resource: Could not connect to the server".
      let errToThrow: any = error;
      const msg = error instanceof Error ? error.message : String(error);
      const isTypeError = typeof TypeError !== 'undefined' && error instanceof TypeError;
      const looksLikeNetworkIssue =
        /Failed to fetch/i.test(msg) ||
        /NetworkError/i.test(msg) ||
        /Load failed/i.test(msg) ||
        /ERR_CONNECTION/i.test(msg) ||
        /Could not connect to the server/i.test(msg);

      if (isTypeError || looksLikeNetworkIssue) {
        errToThrow = new Error('An issue has occurred, try again later');
        // Optionally mark it so callers can differentiate if they need to
        (errToThrow as any).network = true;
      }

      // Swallow logging to avoid noisy browser console; surface via UI middleware instead
      throw errToThrow;
    }
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'GET',
      ...options,
    });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    const headers: Record<string, string> = { ...options?.headers as Record<string, string> };
    
    // Enforce v2 API for signup to enable reCAPTCHA verification
    if (endpoint === '/api/auth/signup') {
      headers['X-API-Version'] = '2.0.0';
    }

    return this.request<T>(endpoint, {
      method: 'POST',
      body: data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined),
      ...options,
      headers
    });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined),
      ...options,
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      ...options,
    });
  }

  /**
   * PATCH request
   */
  async patch<T>(endpoint: string, data?: any, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined),
      ...options,
    });
  }

  /**
   * Uploads a file using multipart/form-data
   */
  async uploadFile<T>(endpoint: string, file: File, fieldName: string = 'file'): Promise<T> {
    const formData = new FormData();
    formData.append(fieldName, file);
    return this.post<T>(endpoint, formData);
  }

  /**
   * Returns the base URL of the API
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }
}

// Create and export the API service instance
export const apiService = new ApiService(API_BASE_URL);

// Export the base URL for use in other parts of the application
export { API_BASE_URL };
export { API_EMAIL_SERVER };